/*
Copyright (c) 2015, salesforce.com, inc. All rights reserved.
Copyright (c) 2017, Picsa Advisory Services (Pty) Ltd. All rights reserved.

Redistribution and use in source and binary forms, with or without modification, are permitted provided that the following conditions are met:
Redistributions of source code must retain the above copyright notice, this list of conditions and the following disclaimer.
Redistributions in binary form must reproduce the above copyright notice, this list of conditions and the following disclaimer in the documentation and/or other materials provided with the distribution.
Neither the name of salesforce.com, inc. nor the names of its contributors may be used to endorse or promote products derived from this software without specific prior written permission.

THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS 'AS IS' AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES LOSS OF USE, DATA, OR PROFITS OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
*/

import React from 'react'
import PropTypes from 'prop-types'

import { Checkbox } from '../'

import { TableHeaderRow } from './TableHeaderRow'
import { TableDataRow } from './TableDataRow'


/*
Note on SORTING options

When using orderBy, changeSort & querySortableColumns props, the component expects sorting of data to
be displayed to rely on the parent component's data query and changes to the query's orderBy parameter
(works well with the default query cursor-based pagination setup)

When using sortedColumnIndex, sortedDirection & also onColumnClick, the component assumes data sorting
functionality is implemented in code for both ascending and descending order across all columns
(works well for unpaginated results, or for when pagination doesn't not performed by the data query)

ROW SELECTION
Add props:
- `selectableRows` (switch to enable selection and show checkboxes on each row)
- `selectedRows` (array containing the current selectedRow ids)
- `onGlobalSelect` (function that handles selection toggle of all/none rows)
- `onRowSelect` (function handles selection of specific rows)

Typical `onGlobalSelect` & `onRowSelect` logic (assuming each row has their id prop set to data record node id):

  onGlobalSelect={() => {
    if (this.state.selectedRows.length === data.edges.length ) {
      this.setState({ selectedRows: [] })  // deselect all
    } else {
      this.setState({ selectedRows: data.edges.map(rr => rr.node.id) })  // select all row ids
    }
  }}
  onRowSelect={e => {
    const id = e.target.id.split('-')[1]  // 'checbox-' removed from id (see Checkbox)
    this.setState(prevState => {
      if (prevState.selectedRows.includes(id)) {
        const selectedRows = [...prevState.selectedRows]
        selectedRows.splice(selectedRows.indexOf(id), 1)  // selected rows minus deselected
        return { selectedRows }
      } else {
        return { selectedRows: [...prevState.selectedRows, id] }
      }
    })
  }}

*/

export class Table extends React.PureComponent {
  static propTypes = {
    id: PropTypes.string,
    columns: PropTypes.arrayOf(PropTypes.string),
    rows: PropTypes.arrayOf(PropTypes.oneOfType([
      PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
      ])).isRequired,
      PropTypes.element
    ])),
    header: PropTypes.element,
    onClick: PropTypes.func,
    objectRows: PropTypes.bool,
    fixedLayout: PropTypes.bool,
    noRowBorders: PropTypes.bool,
    noCellPadding: PropTypes.bool,
    headerCellWrap: PropTypes.bool,
    orderBy: PropTypes.string,  // rename to queryOrderBy?
    changeSort: PropTypes.func,  // rename to changeQueryOrderBy?
    querySortableColumns: PropTypes.object,  // contains column indexes as object keys containing 'direction' and 'onClick' properties
    firstColumnWidth: PropTypes.string,
    onColumnClick: PropTypes.func,
    sortedColumnIndex: PropTypes.number,
    sortedDirection: PropTypes.oneOf(['ascending', 'descending']),
    selectableRows: PropTypes.bool,
    selectedRows: PropTypes.arrayOf(PropTypes.string),
    onGlobalSelect: PropTypes.func,
    onRowSelect: PropTypes.func,
    className: PropTypes.string,
    style: PropTypes.object
  }

  static defaultProps = {
    className: ''
  }

  render() {
    const { id, columns, rows, objectRows, querySortableColumns, orderBy, changeSort, fixedLayout,
      noRowBorders, headerCellWrap, noCellPadding, className, style, header, selectableRows, firstColumnWidth,
      onColumnClick, sortedColumnIndex, sortedDirection, selectedRows, onGlobalSelect, onRowSelect,
      fixedHeader, onClick } = this.props

    let dataRows = []
    const rowsContainTrElements = rows.length > 0 && rows[0].props && rows[0].props.children
    // currently selectableRows will only have an effect if props.rows are <tr> elements

    if (objectRows) {
      dataRows = [...dataRows, ...rows]
      if (selectableRows) {
        dataRows = dataRows.map((row, index) => {
          // props.rows are <tr> elements; necessary at the moment to allow row selection on Table to work
          if (rowsContainTrElements) {
            return (
              <tr key={`dataRow_${row.props.id}`}>
                <td>
                  <Checkbox
                    id={row.props.id}
                    onChange={onRowSelect}
                    checked={selectedRows.includes(row.props.id)}
                  />
                </td>
                {row.props.children}
              </tr>
            )
          }
          else {
            // props.rows are React components (TODO: figure out how to make selectable rows work for React components as props.rows)
            return row
          }
        })
      }
    } else {
      dataRows = [...dataRows, rows.map((row, index) => (
        <TableDataRow
          row={row}
          columns={columns}
          key={`table_data_${index}`}
          onClick={onClick ? onClick : () => false}
        />
      ))]
    }

    return (
      <table
        id={id}
        className={`slds-table ${fixedLayout ? 'slds-table_fixed-layout ' : ''}${noRowBorders ? '' : 'slds-table_bordered '}${noCellPadding ? '' : 'slds-table_cell-buffer '}${className}`} style={style}>
        <thead>
          {columns
            ? <TableHeaderRow
                data={columns}
                querySortableColumns={querySortableColumns}
                orderBy={orderBy}
                changeSort={changeSort}
                cellWrap={headerCellWrap}
                firstColumnWidth={firstColumnWidth}
                onColumnClick={onColumnClick}
                sortedColumnIndex={sortedColumnIndex}
                sortedDirection={sortedDirection}
                onGlobalSelect={selectableRows && rowsContainTrElements
                  ? onGlobalSelect
                  : null
                }
                allRowsSelected={selectableRows && selectedRows.length === rows.length}
                fixedHeader={fixedHeader}
              />
            : header
          }
        </thead>
        <tbody>
          {dataRows}
        </tbody>
      </table>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/table/index.js